import React, { useContext, useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@material-ui/core";
import AppModal from "../AppModal";
import TemplateConversationPreview from "../TemplateConversationPreview";
import api from "../../services/api";
import apiV2 from "../../services/apiV2";
import { ThemeContext } from "../../context/Theme/ThemeContext";
import { resolveUploadedFileUrl } from "../../helpers/resolveMediaUrl";

function countPlaceholders(text) {
	if (!text) return 0;
	return (String(text).match(/\{\{\d+\}\}/g) || []).length;
}

function countTemplateParamsFromComponents(components = []) {
	let total = 0;
	for (const component of components) {
		const type = String(component?.type || "").toUpperCase();
		if (type === "HEADER") {
			const format = String(component?.format || "TEXT").toUpperCase();
			if (format === "TEXT") total += countPlaceholders(component.text);
			continue;
		}
		if (type === "BODY") {
			total += countPlaceholders(component.text);
			continue;
		}
		if (type === "BUTTONS" && Array.isArray(component.buttons)) {
			for (const btn of component.buttons) {
				if (String(btn?.type || "").toUpperCase() !== "URL") continue;
				total += countPlaceholders(btn.url || "");
			}
		}
	}
	return total;
}

function isApprovedTemplate(template) {
	if (!template) return false;
	if (!template.status) return true;
	return String(template.status).toUpperCase() === "APPROVED";
}

function getComponent(template, type) {
	return template?.components?.find(
		(c) => String(c?.type || "").toUpperCase() === type
	);
}

export default function WabaSendTemplateModal({
	open,
	closeModalFn,
	ticketId,
	whatsappId,
}) {
	const { darkMode } = useContext(ThemeContext);
	const [selectedTemplate, setSelectedTemplate] = useState(null);
	const [selectedTemplateRaw, setSelectedTemplateRaw] = useState(null);
	const [errorMessage, setErrorMessage] = useState("");
	const [searchValue, setSearchValue] = useState("");
	const [templates, setTemplates] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [loadingTemplates, setLoadingTemplates] = useState(false);
	const [templateParams, setTemplateParams] = useState([]);
	const [customTemplateId, setCustomTemplateId] = useState("");
	const [mediaPath, setMediaPath] = useState(null);
	const [isHubConnection, setIsHubConnection] = useState(null);
	const [uploadingMedia, setUploadingMedia] = useState(false);

	useEffect(() => {
		if (!open) return undefined;

		let cancelled = false;
		(async () => {
			try {
				const response = await api.get(`/hub-ticketId/${whatsappId}`);
				if (!cancelled) setIsHubConnection(!!response.data);
			} catch {
				if (!cancelled) setIsHubConnection(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [open, whatsappId]);

	useEffect(() => {
		if (!open || isHubConnection == null) return undefined;

		let cancelled = false;
		(async () => {
			setLoadingTemplates(true);
			try {
				let list = [];

				if (isHubConnection) {
					// Conexão Hub: templates vêm do Hub
					const { data } = await api.get(`/hub-tamplate?whatsappId=${whatsappId}`);
					list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
				} else {
					// WhatsApp Cloud oficial
					const response = await api.get(`/WhatsAppMeta/template/${ticketId}`);
					list = Array.isArray(response.data?.data) ? response.data.data : [];
					list = list.filter((template) =>
						String(template?.name || "").includes("notificame")
					);
				}

				if (!cancelled) {
					setTemplates(list.filter(isApprovedTemplate));
				}
			} catch (err) {
				console.error("Erro ao carregar templates:", err);
				if (!cancelled) setTemplates([]);
			} finally {
				if (!cancelled) setLoadingTemplates(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [open, isHubConnection, ticketId, whatsappId]);

	useEffect(() => {
		setMediaPath(null);
		setErrorMessage("");
	}, [selectedTemplate]);

	const filteredTemplates = useMemo(() => {
		const term = searchValue.trim().toLowerCase();
		if (!term) return templates;
		return templates.filter((template) => {
			if (String(template.name || "").toLowerCase().includes(term)) return true;
			return (template.components || []).some(
				(component) =>
					component.text &&
					String(component.text).toLowerCase().includes(term)
			);
		});
	}, [templates, searchValue]);

	const headerFormat = useMemo(() => {
		const header = getComponent(selectedTemplateRaw, "HEADER");
		return String(header?.format || "").toUpperCase();
	}, [selectedTemplateRaw]);

	const requiresMedia = ["VIDEO", "DOCUMENT", "IMAGE"].includes(headerFormat);
	const templateParamsLength = templateParams.length;

	const mediaLabel =
		headerFormat === "IMAGE"
			? "imagem"
			: headerFormat === "VIDEO"
				? "vídeo"
				: headerFormat === "DOCUMENT"
					? "documento"
					: "mídia";

	const getMediaAccept = () => {
		if (headerFormat === "IMAGE") return "image/png, image/jpeg, image/jpg, image/webp";
		if (headerFormat === "VIDEO") return "video/mp4";
		if (headerFormat === "DOCUMENT") return "application/pdf";
		return "";
	};

	const handleSelectTemplate = (template) => {
		setSelectedTemplate(template.id);
		setSelectedTemplateRaw(template);
		setCustomTemplateId("");
		const total = countTemplateParamsFromComponents(template.components || []);
		setTemplateParams(Array(total).fill(""));
	};

	const handleUpdateMedia = async (e) => {
		const file = e.target.files?.[0];
		if (!file) return;

		setUploadingMedia(true);
		try {
			const formData = new FormData();
			formData.append("file", file);
			const response = await api.post("/chatbot/node-upload-image", formData, {
				headers: { "Content-Type": "multipart/form-data" },
			});
			setMediaPath(resolveUploadedFileUrl(file, response));
		} catch (err) {
			console.error(err);
			setErrorMessage("Falha ao anexar a mídia");
		} finally {
			setUploadingMedia(false);
			e.target.value = "";
		}
	};

	const handleClose = () => {
		setErrorMessage("");
		closeModalFn();
	};

	const sendTemplate = async () => {
		if (!selectedTemplateRaw) return;

		if (requiresMedia && !String(mediaPath || "").trim()) {
			setErrorMessage(`Informe a URL ou anexe a ${mediaLabel} do template`);
			return;
		}

		if (
			templateParamsLength > 0 &&
			templateParams.some((p) => !String(p ?? "").trim())
		) {
			setErrorMessage("Preencha todos os parâmetros do template");
			return;
		}

		setIsLoading(true);
		setErrorMessage("");
		try {
			const mediaUrl = String(mediaPath || "").trim() || null;
			let response;

			if (isHubConnection) {
				response = await apiV2.post(`/hub/sendTemplate/${ticketId}`, {
					select: selectedTemplateRaw.name,
					id: selectedTemplateRaw.id,
					templateParams,
					customTemplateId,
					mediaUrl,
				});
			} else {
				response = await api.post(
					`/WhatsAppMeta/template/${ticketId}/${selectedTemplate}`,
					{
						templateParams,
						customTemplateId,
						mediaUrl,
					}
				);
			}

			if (response.status === 201 || response.status === 200) {
				handleClose();
			} else {
				throw new Error(`Erro ${response.status} ao enviar template`);
			}
		} catch (error) {
			setErrorMessage(
				error?.response?.data?.details ||
					error?.response?.data?.error ||
					error?.message ||
					"Erro ao enviar template"
			);
		} finally {
			setIsLoading(false);
		}
	};

	const canSend =
		!!selectedTemplateRaw &&
		!isLoading &&
		(!requiresMedia || !!String(mediaPath || "").trim()) &&
		(templateParamsLength === 0 ||
			templateParams.every((p) => String(p ?? "").trim()));

	const panelBorder = darkMode ? "border-[#3f3f46]" : "border-[#e4e4e7]";
	const panelBg = darkMode ? "bg-[#18181b]" : "bg-white";
	const muted = darkMode ? "text-[#a1a1aa]" : "text-[#71717a]";
	const textMain = darkMode ? "text-[#fafafa]" : "text-[#18181b]";
	const softBg = darkMode ? "bg-[#27272a]" : "bg-[#f4f4f5]";
	const hoverBg = darkMode ? "hover:bg-[#3f3f46]" : "hover:bg-[#f4f4f5]";
	const selectedBg = darkMode ? "bg-primary/20 border-primary" : "bg-primary/10 border-primary";

	return (
		<AppModal
			open={open}
			onClose={handleClose}
			title="Enviar template WhatsApp"
			subtitle="Escolha o template, preencha os parâmetros e confira o preview"
			maxWidth="1100px"
			bodyClassName="!p-0"
			footer={
				<div className="flex w-full flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					{errorMessage ? (
						<p className="m-0 text-sm text-red-500">{errorMessage}</p>
					) : (
						<p className={`m-0 text-sm ${muted}`}>
							{filteredTemplates.length} template(s) disponível(is)
						</p>
					)}
					<div className="flex justify-end gap-2">
						<button type="button" className="btn btn-sub-gray" onClick={handleClose}>
							Cancelar
						</button>
						<button
							type="button"
							className="btn btn-primary min-w-[120px]"
							onClick={sendTemplate}
							disabled={!canSend}
						>
							{isLoading ? <CircularProgress size={18} color="inherit" /> : "Enviar"}
						</button>
					</div>
				</div>
			}
		>
			<div className="grid max-h-[70vh] min-h-[420px] gap-0 lg:grid-cols-[1.1fr_0.9fr]">
				<div className={`flex min-h-0 flex-col gap-3 overflow-auto border-b p-4 lg:border-b-0 lg:border-r ${panelBorder}`}>
					<div className="flex gap-2">
						<input
							type="search"
							className="form-input w-full"
							placeholder="Buscar template..."
							value={searchValue}
							onChange={(e) => setSearchValue(e.target.value)}
							autoFocus
						/>
						{searchValue ? (
							<button
								type="button"
								className="btn btn-sub-gray shrink-0"
								onClick={() => setSearchValue("")}
							>
								Limpar
							</button>
						) : null}
					</div>

					<div className={`min-h-[160px] max-h-[28vh] space-y-2 overflow-auto rounded-xl border p-2 ${panelBorder} ${softBg}`}>
						{loadingTemplates || isHubConnection == null ? (
							<div className="flex items-center justify-center py-10">
								<CircularProgress size={28} />
							</div>
						) : filteredTemplates.length === 0 ? (
							<p className={`m-0 py-8 text-center text-sm ${muted}`}>
								Nenhum template encontrado
							</p>
						) : (
							filteredTemplates.map((template) => {
								const selected = String(selectedTemplate) === String(template.id);
								const body = getComponent(template, "BODY");
								return (
									<button
										key={template.id}
										type="button"
										onClick={() => handleSelectTemplate(template)}
										className={[
											"w-full rounded-xl border px-3 py-3 text-left transition-colors",
											selected ? selectedBg : `${panelBorder} ${panelBg} ${hoverBg}`,
										].join(" ")}
									>
										<div className="flex items-start justify-between gap-2">
											<p className={`m-0 text-sm font-semibold ${textMain}`}>
												{String(template.name || "").replace(/_/g, " ")}
											</p>
											{template.status ? (
												<span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-600 dark:text-emerald-300">
													{template.status}
												</span>
											) : null}
										</div>
										<p className={`mt-1 line-clamp-2 text-xs ${muted}`}>
											{body?.text || "Sem texto de corpo"}
										</p>
									</button>
								);
							})
						)}
					</div>

					{requiresMedia && (
						<div className={`rounded-xl border p-4 ${panelBorder}`}>
							<label className={`mb-2 block text-sm font-medium ${textMain}`}>
								Mídia do template ({mediaLabel})
							</label>
							<div className="flex flex-col gap-2 sm:flex-row">
								<input
									type="url"
									className="form-input min-w-0 flex-1"
									placeholder={`URL da ${mediaLabel} (https://...)`}
									value={mediaPath || ""}
									onChange={(e) =>
										setMediaPath(e.target.value.trim() ? e.target.value : null)
									}
								/>
								<div className="flex shrink-0 gap-2">
									{mediaPath ? (
										<button
											type="button"
											className="btn btn-sub-gray"
											onClick={() => setMediaPath(null)}
										>
											Remover
										</button>
									) : null}
									<label className="btn btn-primary cursor-pointer">
										{uploadingMedia ? "Enviando..." : "Anexar"}
										<input
											type="file"
											className="sr-only"
											accept={getMediaAccept()}
											disabled={uploadingMedia}
											onChange={handleUpdateMedia}
										/>
									</label>
								</div>
							</div>
							{headerFormat === "IMAGE" && mediaPath ? (
								<img
									src={mediaPath}
									alt="Prévia da mídia"
									className="mt-3 max-h-28 rounded-lg object-cover"
								/>
							) : null}
						</div>
					)}

					{selectedTemplateRaw && templateParamsLength > 0 && (
						<div className={`rounded-xl border p-4 ${panelBorder}`}>
							<p className={`mb-3 mt-0 text-sm font-medium ${textMain}`}>
								Parâmetros ({templateParamsLength})
							</p>
							<div className="space-y-3">
								{templateParams.map((value, index) => {
									const isContactName = value === "contactName";
									return (
										<div
											key={`param-${index}`}
											className="flex flex-col gap-2 sm:flex-row sm:items-center"
										>
											<span
												className={`shrink-0 rounded-md px-2 py-1 text-xs font-semibold ${softBg} ${textMain}`}
											>
												{`{{${index + 1}}}`}
											</span>
											<div className={`inline-flex rounded-lg border p-1 ${panelBorder} ${softBg}`}>
												<button
													type="button"
													className={[
														"rounded-md px-3 py-1.5 text-xs font-medium",
														!isContactName
															? "bg-primary text-white"
															: muted,
													].join(" ")}
													onClick={() =>
														setTemplateParams((prev) => {
															const next = [...prev];
															next[index] = "";
															return next;
														})
													}
												>
													Texto livre
												</button>
												<button
													type="button"
													className={[
														"rounded-md px-3 py-1.5 text-xs font-medium",
														isContactName
															? "bg-primary text-white"
															: muted,
													].join(" ")}
													onClick={() =>
														setTemplateParams((prev) => {
															const next = [...prev];
															next[index] = "contactName";
															return next;
														})
													}
												>
													Nome do contato
												</button>
											</div>
											{!isContactName && (
												<input
													type="text"
													className="form-input min-w-0 flex-1"
													placeholder={`Parâmetro ${index + 1}`}
													value={value || ""}
													onChange={(e) => {
														const nextValue = e.target.value;
														setTemplateParams((prev) => {
															const next = [...prev];
															next[index] = nextValue;
															return next;
														});
													}}
												/>
											)}
										</div>
									);
								})}
							</div>
						</div>
					)}
				</div>

				<div className="flex min-h-[320px] p-4 lg:h-full lg:min-h-0">
					<div className="flex h-full w-full">
						<TemplateConversationPreview
							template={selectedTemplateRaw}
							params={templateParams}
							mediaPath={mediaPath}
							darkMode={darkMode}
						/>
					</div>
				</div>
			</div>
		</AppModal>
	);
}
